import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import SlidingPane from 'react-sliding-pane';
import Dropzone from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import client from '../../../shared/api/client';
import { toastError, errorText } from '../../../shared/toastHelper';
import '../styles/disputeEvidence.css';

const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const UPLOAD_CATEGORIES = [
  ['cancellation_policy', 'Cancellation policy'],
  ['customer_communication', 'Customer communication'],
  ['customer_signature', 'Customer signature'],
  ['duplicate_charge_documentation', 'Duplicate charge documentation'],
  ['receipt', 'Receipt'],
  ['refund_policy', 'Refund policy'],
  ['service_documentation', 'Service documentation'],
  ['shipping_documentation', 'Shipping documentation'],
  ['uncategorized_file', 'Other'],
];

const DisputeEvidencePane = ({
  isOpen,
  disputeId,
  packetUrl,
  submitUrl,
  uploadTeamId,
  onClose,
  onSubmitted,
}) => {
  const [packet, setPacket] = useState(null);
  const [draft, setDraft] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingMutations, setPendingMutations] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!isOpen || !packetUrl) {
      return undefined;
    }

    sessionRef.current += 1;
    const session = sessionRef.current;
    const isStale = () => sessionRef.current !== session;

    setPacket(null);
    setLoadFailed(false);
    setSubmitted(false);
    setAgreed(false);
    setUploads([]);
    setUploadingFiles([]);
    setPendingMutations(0);
    setIsSubmitting(false);

    client
      .get(packetUrl)
      .then(({ data }) => {
        if (isStale()) {
          return;
        }
        setPacket(data);
        setDraft(data.uncategorized_text || '');
        setUploads(data.uploads || []);
      })
      .catch((e) => {
        if (isStale()) {
          return;
        }
        setLoadFailed(true);
        toastError(errorText(e, 'Could not assemble the evidence packet for this dispute.'));
      });

    return () => {
      sessionRef.current += 1;
    };
  }, [isOpen, packetUrl]);

  const canSubmit =
    packet &&
    draft.trim().length > 0 &&
    agreed &&
    !isSubmitting &&
    uploadingFiles.length === 0 &&
    pendingMutations === 0 &&
    uploads.every((u) => u.category && u.category.length > 0);

  const submit = () => {
    const session = sessionRef.current;
    const isStale = () => sessionRef.current !== session;
    setIsSubmitting(true);
    client
      .post(submitUrl, {
        payment_dispute: { one_time_submission_agreement: agreed, uncategorized_text: draft },
      })
      .then(({ data }) => {
        if (isStale()) {
          return;
        }
        setSubmitted(true);
        if (onSubmitted) {
          onSubmitted(data);
        }
      })
      .catch((e) => {
        if (!isStale()) {
          toastError(errorText(e, 'Submission failed.'));
        }
      })
      .finally(() => {
        if (!isStale()) {
          setIsSubmitting(false);
        }
      });
  };

  const uploadFile = (file) => {
    const session = sessionRef.current;
    const isStale = () => sessionRef.current !== session;
    const pending = { uuid: uuidv4(), name: file.name };
    setUploadingFiles((files) => [...files, pending]);

    const formData = new FormData();
    formData.append('payment_dispute_upload[file]', file);
    formData.append('payment_dispute_upload[payment_dispute_id]', disputeId);

    client
      .post(`/teams/${uploadTeamId}/payment_dispute_uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(({ data }) => {
        if (isStale()) {
          return;
        }
        setUploads((current) => [...current, data]);
        setUploadingFiles((files) => files.filter((f) => f.uuid !== pending.uuid));
      })
      .catch((e) => {
        if (isStale()) {
          return;
        }
        toastError(errorText(e, 'That file could not be uploaded.'));
        setUploadingFiles((files) => files.filter((f) => f.uuid !== pending.uuid));
      });
  };

  const setCategory = (upload, category) => {
    const session = sessionRef.current;
    const isStale = () => sessionRef.current !== session;
    const priorCategory = upload.category;
    setUploads((current) => current.map((u) => (u.id === upload.id ? { ...u, category } : u)));
    setPendingMutations((n) => n + 1);
    client
      .patch(`/teams/${uploadTeamId}/payment_dispute_uploads/${upload.id}`, {
        payment_dispute_upload: { category },
      })
      .catch((e) => {
        if (isStale()) {
          return;
        }
        toastError(errorText(e, 'That category could not be saved.'));
        setUploads((current) => current.map((u) => (u.id === upload.id ? { ...u, category: priorCategory } : u)));
      })
      .finally(() => {
        if (!isStale()) {
          setPendingMutations((n) => n - 1);
        }
      });
  };

  const removeUpload = (upload) => {
    const session = sessionRef.current;
    const isStale = () => sessionRef.current !== session;
    setUploads((current) => current.filter((u) => u.id !== upload.id));
    setPendingMutations((n) => n + 1);
    client
      .delete(`/teams/${uploadTeamId}/payment_dispute_uploads/${upload.id}`)
      .catch((e) => {
        if (isStale()) {
          return;
        }
        toastError(errorText(e, 'That file could not be removed.'));
        setUploads((current) => (current.some((u) => u.id === upload.id) ? current : [...current, upload]));
      })
      .finally(() => {
        if (!isStale()) {
          setPendingMutations((n) => n - 1);
        }
      });
  };

  const usedCategory = (category) => uploads.some((u) => u.category === category);

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <SlidingPane
      isOpen={isOpen}
      className="sliding-pane-container-full-height"
      overlayClassName="dispute-evidence-overlay"
      width="460px"
      hideHeader
      onRequestClose={requestClose}
    >
      <div className="dispute-evidence-pane">
        <div className="dispute-evidence-header">
          <strong>Counter dispute</strong>
          <a
            href="#"
            aria-label="Close"
            onClick={(e) => {
              e.preventDefault();
              requestClose();
            }}
          >
            <i className="fas fa-times" />
          </a>
        </div>

        {submitted ? (
          <div className="dispute-evidence-submitted">
            <p className="mb-1"><strong>Evidence submitted to Stripe</strong></p>
            <p className="mb-0 small">
              The response is now with the cardholder&apos;s issuing bank. A decision can take up to three months.
            </p>
          </div>
        ) : null}

        {!packet && !loadFailed && !submitted ? <div className="dispute-evidence-loading">Assembling evidence…</div> : null}

        {loadFailed && !submitted ? (
          <div className="dispute-evidence-loading">Could not load this dispute&apos;s evidence — close and reopen to retry.</div>
        ) : null}

        {packet && !submitted ? (
          <>
            <div className="dispute-evidence-summary">
              <span>{formatter.format(packet.amount)}</span>
              <span className="dispute-evidence-reason">{(packet.reason || '').replace(/_/g, ' ')}</span>
            </div>

            {packet.flags.length > 0 ? (
              <div className="dispute-evidence-flags">
                <p className="dispute-evidence-flags-title">Read before submitting</p>
                <ul>
                  {packet.flags.map((flag, i) => (
                    <li key={i}>{flag}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <label className="dispute-evidence-label" htmlFor="dispute-evidence-draft">
              Response to the cardholder&apos;s claim
            </label>
            <p className="dispute-evidence-hint">
              Drafted from this order. Edit anything that does not read true before submitting.
            </p>
            <textarea
              id="dispute-evidence-draft"
              className="form-control"
              rows="6"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />

            <p className="dispute-evidence-section-title">Evidence we&apos;ll send</p>
            <table className="dispute-evidence-fields">
              <tbody>
                {packet.fields.map((field) => (
                  <tr key={field.key}>
                    <td className="dispute-evidence-field-label">{field.label}</td>
                    <td className={field.value ? '' : 'dispute-evidence-field-missing'}>
                      {field.value || 'Not on file'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {uploadTeamId ? (
              <div className="dispute-evidence-uploads">
                <p className="dispute-evidence-section-title">Attachments</p>
                <p className="dispute-evidence-hint">
                  Add the refund policy or customer messages if you have them — the dashboard doesn&apos;t hold these.
                </p>
                {uploads.map((upload) => (
                  <div className="dispute-evidence-upload" key={upload.id}>
                    <span className="dispute-evidence-upload-name">{upload.file_file_name}</span>
                    <select
                      className="form-control form-control-sm"
                      value={upload.category || ''}
                      onChange={(e) => setCategory(upload, e.target.value)}
                    >
                      <option value="">Choose category</option>
                      {UPLOAD_CATEGORIES.map(([value, label]) => (
                        <option key={value} value={value} disabled={usedCategory(value) && upload.category !== value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); removeUpload(upload); }}>
                      <i className="far fa-times" />
                    </a>
                  </div>
                ))}
                {uploadingFiles.map((f) => (
                  <div className="dispute-evidence-upload" key={f.uuid}>
                    <span className="dispute-evidence-upload-name">{f.name}</span>
                    <img src="/uploading-loading.gif" style={{ width: '18px' }} alt="Uploading" />
                  </div>
                ))}
                <Dropzone accept="application/pdf, image/jpeg, image/png" onDrop={(files) => files.forEach(uploadFile)}>
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className="dispute-evidence-dropzone">
                      <input {...getInputProps()} />
                      <a className="btn btn-sm btn-outline-primary" href="#" onClick={(e) => e.preventDefault()}>
                        <i className="fas fa-arrow-up mr-2" />
                        Upload a file
                      </a>
                      <span className="small ml-2">or drag and drop</span>
                    </div>
                  )}
                </Dropzone>
              </div>
            ) : null}

            <div className="custom-control custom-checkbox dispute-evidence-agreement">
              <input
                type="checkbox"
                className="custom-control-input"
                id="dispute-evidence-agreement"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="dispute-evidence-agreement">
                I understand this evidence can only be submitted to the card issuer once.
              </label>
            </div>

            <button type="button" className="btn btn-primary btn-block" disabled={!canSubmit} onClick={submit}>
              {isSubmitting ? 'Submitting evidence…' : 'Submit evidence'}
            </button>
          </>
        ) : null}
      </div>
    </SlidingPane>
  );
};

DisputeEvidencePane.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  disputeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  packetUrl: PropTypes.string,
  submitUrl: PropTypes.string,
  uploadTeamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func,
};

DisputeEvidencePane.defaultProps = {
  disputeId: null,
  packetUrl: null,
  submitUrl: null,
  uploadTeamId: null,
  onSubmitted: null,
};

export default DisputeEvidencePane;
